import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { bookingAPI, userAPI, serviceAPI, sessionBundleAPI } from '../services/api';
import { useI18n } from '../i18n/i18nContext';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useUser } from '../contexts/UserContext';
import { getTranslatedField } from '../utils/translationUtils';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
  UserProfile,
  SessionBundle,
  Booking,
  UserSessionBundle,
  UserSubscription,
} from '@shared/types';
import { BOOKING_STATUS } from '@shared/constants';
import { formatInTimeZone } from 'date-fns-tz';
import { ru as ruLocale, et as etLocale, fi as fiLocale } from 'date-fns/locale';
import { PhoneInput } from '../components/PhoneInput';
import { formatPrice } from '@shared/utils';
import BirthdayPicker from '../components/BirthdayPicker';

type TabType = 'profile' | 'sessionBundles' | 'bookings';

const TALLINN_TIMEZONE = 'Europe/Tallinn';

interface SessionBundleDefinition {
  id: string;
  name: string;
  name_et?: string;
  name_ru?: string;
  name_fi?: string;
  type: 'bulk' | 'subscription';
  // Add other fields as needed
}

const AccountPage: React.FC = () => {
  const { logout } = useAuth0();
  const { user, setUser, isLoadingUser } = useUser();
  const location = useLocation();
  const navigate = useLocalizedNavigate();
  const { t, language } = useI18n();
  useDocumentTitle(t.account.title);
  const [activeTab, setActiveTab] = useState<TabType>('profile');

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<Booking | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Coupon pair cancellation states
  const [couponPairStatus, setCouponPairStatus] = useState<{
    isCouponSession: boolean;
    linkedBookings: Array<{ id: string; status: string; startTime: string; isUsed: boolean }>;
    canCancelPair: boolean;
    requiresPairCancellation: boolean;
    refundAmountCents: number;
    hasUsedSession: boolean;
    message: string;
  } | null>(null);
  const [loadingCouponStatus, setLoadingCouponStatus] = useState(false);

  const [contracts, setContracts] = useState<SessionBundle[]>([]);
  const [packagePacks, setPackagePacks] = useState<SessionBundle[]>([]);
  const [contractsCount, setContractsCount] = useState(0);
  const [loadingContracts, setLoadingContracts] = useState(false);

  // Booking filter states
  const [bookingFilter, setBookingFilter] = useState<'all' | 'upcoming' | 'past' | 'cancelled'>(
    'upcoming'
  );
  const [searchTerm, setSearchTerm] = useState('');

  // Subscription cancellation states
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState<SessionBundle | null>(null);
  const [cancelSubscriptionError, setCancelSubscriptionError] = useState<string | null>(null);

  // Profile state
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailChangeRequested, setEmailChangeRequested] = useState(false);
  const [promoPreference, setPromoPreference] = useState(false);

  // State for phone number management
  const [phoneValue, setPhoneValue] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [spouseEmail, setSpouseEmail] = useState('');
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  // State for gender and birthday
  const [gender, setGender] = useState<'male' | 'female' | ''>('');
  const [birthday, setBirthday] = useState('');
  const [editingGender, setEditingGender] = useState(false);
  const [editingBirthday, setEditingBirthday] = useState(false);

  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySms, setNotifySms] = useState(true);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);

  // Check for success message from navigation
  useEffect(() => {
    const state = location.state as { message?: string };
    if (state?.message) {
      setSuccessMessage(state.message);
      setTimeout(() => setSuccessMessage(''), 5000);
    }

    // Check if user just completed registration
    if (localStorage.getItem('just_registered') === 'true') {
      setSuccessMessage(
        t.account.welcomeMessage ||
          `Thank you for signing up at Fire & Ice! We're thrilled to have you join our wellness community. Take a moment to explore your account and discover all the amazing services we offer.`
      );
      localStorage.removeItem('just_registered');
      setTimeout(() => setSuccessMessage(''), 8000);
    }
  }, [location.state, t.account.welcomeMessage]);

  useEffect(() => {
    // Only fetch data after AuthTokenManager finishes setting up the token
    if (!isLoadingUser) {
      fetchBookings();
      fetchUserContracts();
      fetchUserProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingUser]);

  useEffect(() => {
    if (user?.metaData) {
      setNotifyEmail(user.metaData.notifyByEmail ?? true);
      setNotifySms(user.metaData.notifyBySms ?? true);
    }
  }, [user]);

  const fetchBookings = async () => {
    setLoadingBookings(true);
    try {
      const now = new Date();
      const fromDate = new Date(now.getFullYear() - 2, now.getMonth(), 1);
      const toDate = new Date(now.getFullYear() + 2, now.getMonth() + 1, 0);

      const params = {
        from: fromDate.toISOString(),
        to: toDate.toISOString(),
        limit: 500,
      };

      const [bookingsResponse, servicesResponse] = await Promise.all([
        bookingAPI.getAll(params),
        serviceAPI.getAll(),
      ]);

      // Create a map of services for quick lookup
      const servicesMap = new Map();
      servicesResponse.data.forEach((service: any) => {
        servicesMap.set(service.id, service);
      });

      // Transform API response to expected format
      const transformedBookings = bookingsResponse.data.map((booking: any) => {
        // Extract date and time from startTime and convert to Tallinn timezone
        const startDateTime = new Date(booking.startTime);

        // Use formatInTimeZone to correctly convert UTC to Tallinn timezone
        const date = formatInTimeZone(startDateTime, TALLINN_TIMEZONE, 'yyyy-MM-dd');
        const time = formatInTimeZone(startDateTime, TALLINN_TIMEZONE, 'HH:mm');

        // Get service information
        const service = servicesMap.get(booking.serviceId);

        return {
          ...booking,
          date,
          time,
          participants: booking.spots || booking.participants || 1,
          service: service || {
            name: 'Service',
            name_et: 'Teenus',
            name_ru: 'Услуга',
            name_fi: 'Palvelu',
            duration: 60,
          },
        };
      });

      setBookings(transformedBookings);
    } catch (err) {
      console.error(t.console.errors.fetchingBookings, err);
      // Don't fallback to mock data, just show empty state
      setBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  };

  const fetchUserContracts = async () => {
    setLoadingContracts(true);
    try {
      // First, fetch all available session bundle definitions from Bookla
      const sessionBundleDefinitionsResponse = await sessionBundleAPI.getAll();
      const sessionBundleDefinitions: SessionBundleDefinition[] = (
        sessionBundleDefinitionsResponse.data as any
      ).data.sessionBundles;

      // Create a map for quick lookup by ID
      const sessionBundleDefMap = new Map<string, SessionBundleDefinition>(
        sessionBundleDefinitions.map((sessionBundle: SessionBundleDefinition) => [
          sessionBundle.id,
          sessionBundle,
        ])
      );

      // Then fetch user's active contracts (session bundles, subscriptions, and package-packs)
      const [sessionBundlesResponse, subscriptionsResponse, packagePacksResponse] =
        await Promise.all([
          userAPI.getSessionBundles(),
          userAPI.getSubscriptions(),
          userAPI.getPackagePacks(),
        ]);

      // Combine all user contracts
      const allUserContracts = [...sessionBundlesResponse.data, ...subscriptionsResponse.data];

      // Enrich each contract with the type from session bundle definitions
      const enrichedContracts = allUserContracts.map(
        (contract: UserSessionBundle | UserSubscription) => {
          const subscriptionId = (
            'subscriptionID' in contract
              ? contract.subscriptionID
              : (contract as UserSessionBundle).session_bundle_id
          ) as string;
          const sessionBundleDef = sessionBundleDefMap.get(subscriptionId);
          if (sessionBundleDef) {
            return {
              ...sessionBundleDef,
              ...contract,
            } as any as SessionBundle;
          }
          return contract as any as SessionBundle;
        }
      );

      const mappedPackagePacks = (packagePacksResponse.data || []) as SessionBundle[];

      setContracts(enrichedContracts);
      setPackagePacks(mappedPackagePacks);
      setContractsCount(enrichedContracts.length + mappedPackagePacks.length);
    } catch (err) {
      console.error(t.console.errors.fetchingUserSessionBundles, err);
    } finally {
      setLoadingContracts(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await userAPI.getProfile();
      setUserProfile(response.data);

      setName(response.data.name || '');
      setEmail(response.data.email || '');
      setPromoPreference(response.data.promoPreference || false);

      // Initialize phone field from profile
      if (response.data.phoneNumber) {
        setPhoneValue(response.data.phoneNumber);
      }

      // Initialize gender and birthday from profile
      if (response.data.gender) {
        setGender(response.data.gender);
      }
      if (response.data.birthday) {
        setBirthday(response.data.birthday);
      }

      // Initialize spouse email from profile
      if (response.data.spouseEmail) {
        setSpouseEmail(response.data.spouseEmail);
      }

      // Initialize notification preferences from profile response
      setNotifyEmail(response.data.notifyByEmail ?? true);
      setNotifySms(response.data.notifyBySms ?? false);
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  };

  const openCancelModal = async (booking: Booking) => {
    setBookingToCancel(booking);
    setCancelError(null);
    setCouponPairStatus(null);
    setIsCancelModalOpen(true);

    // Check if this is a coupon session and fetch linked booking status
    if (booking.isCouponSession || booking.couponCodeId) {
      setLoadingCouponStatus(true);
      try {
        const response = await bookingAPI.getCouponStatus(booking.id);
        setCouponPairStatus(response.data);
      } catch (err) {
        console.error('Error fetching coupon status:', err);
        // If we can't fetch status, allow normal cancellation
        setCouponPairStatus(null);
      } finally {
        setLoadingCouponStatus(false);
      }
    }
  };

  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
    setBookingToCancel(null);
    setCouponPairStatus(null);
    setLoadingCouponStatus(false);
  };

  const isWithin24Hours = (date: string, time: string) => {
    const bookingDateTime = new Date(`${date}T${time}`);
    const now = new Date();
    const hoursDifference = (bookingDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursDifference < 24 && hoursDifference > 0;
  };

  const handleConfirmCancel = async () => {
    if (!bookingToCancel) return;

    setIsCancelling(true);
    setCancelError(null);
    try {
      // Check if this is a coupon pair cancellation
      if (couponPairStatus?.requiresPairCancellation && couponPairStatus?.canCancelPair) {
        // Use coupon pair cancellation endpoint
        const result = await bookingAPI.cancelCouponPair(bookingToCancel.id, language);
        const refundAmount = result.data.refundAmountCents / 100;
        setSuccessMessage(
          (
            t.coupon?.pairCancellationSuccess ||
            'Both coupon sessions cancelled successfully. {{amount}} refunded. Coupon restored for reuse.'
          ).replace('{{amount}}', formatPrice(refundAmount))
        );
      } else {
        // Normal cancellation
        await bookingAPI.cancel(bookingToCancel.id, language);
        setSuccessMessage(t.account.bookingCancelledSuccess);
      }
      setTimeout(() => setSuccessMessage(''), 5000);
      closeCancelModal();
      fetchBookings(); // Refetch bookings to update the list
    } catch (err: any) {
      console.error(t.console.errors.cancellingBooking, err);
      // Check if it's a 24-hour policy violation
      if (err.response?.data?.error?.includes('24 hours')) {
        setCancelError(
          t.account.cancellationPolicy24Hours ||
            'Cannot cancel booking within 24 hours of appointment'
        );
      } else if (err.response?.data?.data?.reason === 'SESSION_ALREADY_USED') {
        setCancelError(
          t.coupon?.errors?.sessionAlreadyUsed ||
            'Cannot cancel: one or more sessions have already been used. Rescheduling may still be possible.'
        );
      } else {
        setCancelError(t.account.bookingCancelFailed);
      }
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReschedule = (booking: Booking) => {
    // Navigate to booking calendar page with booking data
    const stateData = {
      isRescheduling: true,
      bookingId: booking.id,
      currentDate: booking.date,
      currentTime: booking.time,
    };
    navigate(`/book/${booking.serviceId}`, {
      state: stateData,
    });
  };

  const savePhone = async () => {
    if (!isPhoneValid || !phoneValue) {
      setPhoneError(t.account.phoneNumberInvalid);
      return;
    }

    try {
      await userAPI.updateProfile({ phoneNumber: phoneValue });

      // Update local state
      if (userProfile) {
        setUserProfile({ ...userProfile, phoneNumber: phoneValue });
      }

      setEditingPhone(false);
      setPhoneError(null);
      setSuccessMessage(t.account.phoneNumberUpdated);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating phone number:', error);
      setPhoneError('Failed to update phone number. Please try again.');
    }
  };

  const saveGender = async () => {
    try {
      await userAPI.updateProfile({ gender: gender || null });
      if (userProfile) {
        setUserProfile({ ...userProfile, gender: gender || undefined });
      }
      setEditingGender(false);
      setSuccessMessage(t.common.success);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating gender:', error);
    }
  };

  const saveBirthday = async () => {
    try {
      await userAPI.updateProfile({ birthday: birthday || null });
      if (userProfile) {
        setUserProfile({ ...userProfile, birthday: birthday || undefined });
      }
      setEditingBirthday(false);
      setSuccessMessage(t.common.success);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating birthday:', error);
    }
  };

  const saveName = async () => {
    if (!name.trim()) {
      setNameError(t.account.nameRequired || 'Name cannot be empty.');
      return;
    }
    try {
      await userAPI.updateProfile({ name });
      if (userProfile) {
        setUserProfile({ ...userProfile, name });
      }
      setEditingName(false);
      setNameError(null);
      setSuccessMessage(t.account.nameUpdated || 'Name updated successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating name:', error);
      setNameError('Failed to update name. Please try again.');
    }
  };

  const saveEmail = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError(t.account.emailInvalid || 'Please enter a valid email address.');
      return;
    }
    if (email === userProfile?.email) {
      setEditingEmail(false);
      return;
    }
    setEmailChangeRequested(false);
    try {
      await userAPI.updateProfile({ email });
      setEditingEmail(false);
      setEmailError(null);
      setEmailChangeRequested(true);
    } catch (error) {
      console.error('Error updating email:', error);
      setEmailError('Failed to initiate email change. Please try again.');
    }
  };

  const savePromoPreference = async (value: boolean) => {
    try {
      await userAPI.updateProfile({ promoPreference: value });
      setPromoPreference(value);
      if (userProfile) {
        setUserProfile({ ...userProfile, promoPreference: value });
      }
      setSuccessMessage(t.account.preferencesUpdated || 'Preferences updated successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating promo preference:', error);
    }
  };

  const handleConfirmCancelSubscription = async () => {
    if (!subscriptionToCancel) {
      setCancelSubscriptionError('No subscription selected');
      return;
    }

    setIsCancellingSubscription(true);
    setCancelSubscriptionError(null);
    try {
      const contractId = subscriptionToCancel.id;
      if (!contractId) {
        throw new Error('Contract ID not found');
      }

      const {
        data: { ends_at },
      } = await userAPI.cancelSubscription(contractId);

      setShowCancelSubscriptionModal(false);
      setSubscriptionToCancel(null);
      await fetchUserContracts();
      setSuccessMessage(
        t.account.cancelSuccess.replace(
          '{{endDate}}',
          new Date(ends_at).toLocaleDateString(language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        )
      );
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error(err);
      setCancelSubscriptionError(t.account.cancelError);
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const handleSpouseInvite = async () => {
    try {
      await userAPI.updateProfile({ spouseEmail });

      // Update local state
      if (userProfile) {
        setUserProfile({ ...userProfile, spouseEmail });
      }

      // Update user context to reflect pending status immediately
      if (user) {
        setUser({
          ...user,
          metaData: {
            ...user.metaData,
            spouse_status: 'pending',
            spouse_email: spouseEmail,
            spouse_role: 'inviter',
            spouse_invite_created_at: new Date().toISOString(),
          },
        });
      }

      setSuccessMessage(t.account.invitationSent);
      setTimeout(() => setSuccessMessage(''), 3000);
      setSpouseEmail('');
    } catch (error) {
      console.error('Error saving spouse email:', error);
      setSuccessMessage('Failed to save spouse email. Please try again.');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleSavePreferences = async (email: boolean, sms: boolean) => {
    setIsSavingPreferences(true);
    try {
      const newMetaData = await userAPI.updateUserPreferences({
        notifyByEmail: email,
        notifyBySms: sms,
      });

      if (user && setUser) {
        setUser({ ...user, metaData: newMetaData });
      }

      setSuccessMessage('Notification preferences updated successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Failed to save preferences', error);
      setNotifyEmail(user?.metaData?.notifyByEmail ?? true);
      setNotifySms(user?.metaData?.notifyBySms ?? true);
      setSuccessMessage('Failed to save preferences. Please try again.');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
    setIsSavingPreferences(false);
  };

  const handleEmailChange = (isChecked: boolean) => {
    if (!isChecked && !notifySms) {
      setSuccessMessage('Cannot disable all notification methods. At least one must be enabled.');
      setTimeout(() => setSuccessMessage(''), 3000);
      return;
    }
    setNotifyEmail(isChecked);
    handleSavePreferences(isChecked, notifySms);
  };

  const handleSmsChange = (isChecked: boolean) => {
    if (!isChecked && !notifyEmail) {
      setSuccessMessage('Cannot disable all notification methods. At least one must be enabled.');
      setTimeout(() => setSuccessMessage(''), 3000);
      return;
    }
    setNotifySms(isChecked);
    handleSavePreferences(notifyEmail, isChecked);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const dateLocale =
      language === 'ru'
        ? ruLocale
        : language === 'et'
          ? etLocale
          : language === 'fi'
            ? fiLocale
            : undefined;
    return formatInTimeZone(date, TALLINN_TIMEZONE, 'EEE, MMMM d, yyyy', { locale: dateLocale });
  };

  const formatTime = (timeStr: string) => {
    return timeStr;
  };

  const upcomingBookings = bookings
    .filter((booking) => {
      const bookingDateTime = new Date(`${booking.date}T${booking.time}`);
      return bookingDateTime >= new Date() && booking.status === BOOKING_STATUS.CONFIRMED;
    })
    .sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.time}`);
      const dateB = new Date(`${b.date}T${b.time}`);
      return dateA.getTime() - dateB.getTime();
    });

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">{t.account.name}</label>
        {editingName ? (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`w-full rounded-md border px-3 py-2 ${
                    nameError ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
              </div>
              <button
                onClick={saveName}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
              >
                {t.account.save}
              </button>
              <button
                onClick={() => {
                  setName(userProfile?.name || '');
                  setEditingName(false);
                  setNameError(null);
                }}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
              >
                {t.account.cancel}
              </button>
            </div>
            {nameError && <p className="text-sm text-red-600">{nameError}</p>}
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-gray-900">{userProfile?.name || t.account.notProvided}</span>
            <button
              onClick={() => setEditingName(true)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {t.account.edit}
            </button>
          </div>
        )}
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">{t.account.email}</label>
        {editingEmail ? (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full rounded-md border px-3 py-2 ${
                  emailError ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              <button
                onClick={saveEmail}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
              >
                {t.account.save}
              </button>
              <button
                onClick={() => {
                  setEditingEmail(false);
                  setEmail(userProfile?.email || '');
                  setEmailError(null);
                }}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
              >
                {t.account.cancel}
              </button>
            </div>
            {emailError && <p className="text-sm text-red-600">{emailError}</p>}
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-gray-900">{userProfile?.email}</span>
            <button
              onClick={() => {
                setEmail(userProfile?.email || '');
                setEditingEmail(true);
              }}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {t.account.edit}
            </button>
          </div>
        )}
        {emailChangeRequested && (
          <div className="mt-4 rounded border border-green-400 bg-green-100 p-4 text-green-700">
            <strong>{t.common.success}!</strong>
            <p>{t.account.emailChangeSubmitted}</p>
          </div>
        )}
      </div>

      <div>
        <div className="flex items-start">
          <div className="flex h-5 items-center">
            <input
              id="promoPreference"
              name="promoPreference"
              type="checkbox"
              checked={promoPreference}
              onChange={(e) => savePromoPreference(e.target.checked)}
              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="promoPreference" className="cursor-pointer font-medium text-gray-700">
              {t.account.promoOptIn ||
                'I would like to receive promotional emails and newsletters.'}
            </label>
          </div>
        </div>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          {t.account.phoneNumber}
        </label>
        {editingPhone ? (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="flex-1">
                <PhoneInput
                  value={phoneValue}
                  onChange={setPhoneValue}
                  onValidation={setIsPhoneValid}
                  error={phoneError}
                  placeholder="Phone number"
                  required
                />
              </div>
              <button
                onClick={savePhone}
                disabled={!isPhoneValid || !phoneValue}
                className={`rounded-md px-4 py-2 text-sm font-medium ${
                  isPhoneValid && phoneValue
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'cursor-not-allowed bg-gray-300 text-gray-500'
                }`}
              >
                {t.account.save}
              </button>
              <button
                onClick={() => {
                  // Reset to profile phone number
                  setPhoneValue(userProfile?.phoneNumber || '');
                  setEditingPhone(false);
                  setPhoneError(null);
                }}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
              >
                {t.account.cancel}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-gray-900">
              {userProfile?.phoneNumber || t.account.notProvided}
            </span>
            <button
              onClick={() => setEditingPhone(true)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {t.account.edit}
            </button>
          </div>
        )}
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">{t.account.gender}</label>
        {editingGender ? (
          <div className="flex items-center space-x-2">
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value as 'male' | 'female' | '')}
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
            >
              <option value="">{t.registration.genderPlaceholder}</option>
              <option value="male">{t.registration.male}</option>
              <option value="female">{t.registration.female}</option>
            </select>
            <button
              onClick={saveGender}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              {t.account.save}
            </button>
            <button
              onClick={() => {
                setGender((userProfile?.gender as 'male' | 'female' | '') || '');
                setEditingGender(false);
              }}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
            >
              {t.account.cancel}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-gray-900">
              {gender === 'male'
                ? t.registration.male
                : gender === 'female'
                  ? t.registration.female
                  : t.account.notProvided}
            </span>
            <button
              onClick={() => setEditingGender(true)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {t.account.edit}
            </button>
          </div>
        )}
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">{t.account.birthday}</label>
        {editingBirthday ? (
          <div className="flex items-center space-x-2">
            <div className="flex-1">
              <BirthdayPicker value={birthday} onChange={(value) => setBirthday(value)} />
            </div>
            <button
              onClick={saveBirthday}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              {t.account.save}
            </button>
            <button
              onClick={() => {
                setBirthday(userProfile?.birthday || '');
                setEditingBirthday(false);
              }}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
            >
              {t.account.cancel}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-gray-900">
              {birthday
                ? new Date(birthday).toLocaleDateString(language, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : t.account.notProvided}
            </span>
            <button
              onClick={() => setEditingBirthday(true)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {t.account.edit}
            </button>
          </div>
        )}
      </div>

      <div className="border-t pt-6">
        <h3 className="mb-4 text-lg font-semibold">{t.account.notificationPreferences}</h3>
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="notifyEmail"
              checked={notifyEmail}
              onChange={(e) => handleEmailChange(e.target.checked)}
              disabled={isSavingPreferences}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="notifyEmail" className="ml-3 block text-sm text-gray-700">
              {t.account.receiveEmailReminders} ({userProfile?.email})
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="notifySms"
              checked={notifySms}
              onChange={(e) => handleSmsChange(e.target.checked)}
              disabled={isSavingPreferences || !userProfile?.phoneNumber}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="notifySms" className="ml-3 block text-sm text-gray-700">
              {t.account.receiveSmsReminders} ({userProfile?.phoneNumber || t.account.noPhoneNumber}
              )
            </label>
            {!userProfile?.phoneNumber && (
              <span className="ml-2 text-xs text-gray-500">{t.account.addPhoneToEnable}</span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 border-t pt-6">
        <h3 className="mb-4 text-lg font-semibold">{t.account.spouse.title}</h3>
        {(() => {
          const spouseMetaData = user?.metaData || {};
          const spouseStatus = spouseMetaData.spouse_status;
          const spouseEmailMeta = spouseMetaData.spouse_email;

          if (spouseStatus === 'linked') {
            return (
              <div className="rounded-md border border-green-200 bg-green-50 p-4">
                <p className="text-sm text-gray-700">
                  {t.account.spouse.linked.replace('{{email}}', spouseEmailMeta || '')}
                </p>
              </div>
            );
          } else if (spouseStatus === 'pending') {
            return (
              <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4">
                <p className="text-sm text-gray-700">
                  {t.account.spouse.pending.replace('{{email}}', spouseEmailMeta || '')}
                </p>
              </div>
            );
          } else if (spouseStatus === 'declined') {
            return (
              <div className="space-y-4">
                <div className="rounded-md border border-red-200 bg-red-50 p-4">
                  <p className="text-sm text-gray-700">
                    {t.account.spouse.declined.replace('{{email}}', spouseEmailMeta || '')}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="email"
                    value={spouseEmail}
                    onChange={(e) => setSpouseEmail(e.target.value)}
                    placeholder={spouseEmailMeta || 'spouse@email.com'}
                    className="flex-1 rounded-md border border-gray-300 px-3 py-2"
                  />
                  <button
                    onClick={handleSpouseInvite}
                    disabled={!spouseEmail.includes('@')}
                    className={`rounded-md px-4 py-2 text-sm font-medium ${
                      spouseEmail.includes('@')
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'cursor-not-allowed bg-gray-300 text-gray-500'
                    }`}
                  >
                    {t.account.save}
                  </button>
                </div>
              </div>
            );
          } else {
            return (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">{t.account.spouse.invite}</p>
                <div className="flex items-center space-x-2">
                  <input
                    type="email"
                    value={spouseEmail}
                    onChange={(e) => setSpouseEmail(e.target.value)}
                    placeholder="spouse@email.com"
                    className="flex-1 rounded-md border border-gray-300 px-3 py-2"
                  />
                  <button
                    onClick={handleSpouseInvite}
                    disabled={!spouseEmail.includes('@')}
                    className={`rounded-md px-4 py-2 text-sm font-medium ${
                      spouseEmail.includes('@')
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'cursor-not-allowed bg-gray-300 text-gray-500'
                    }`}
                  >
                    {t.account.save}
                  </button>
                </div>
              </div>
            );
          }
        })()}
      </div>
    </div>
  );

  const renderSessionBundlesAndSubscriptionsTab = () => {
    const subscriptions = contracts.filter(
      (sessionBundle) =>
        sessionBundle.type === 'subscription' || (sessionBundle.recurring && !sessionBundle.type)
    );
    const bulkSessionBundles = contracts.filter(
      (sessionBundle) =>
        sessionBundle.type === 'bulk' || (!sessionBundle.recurring && !sessionBundle.type)
    );

    const activeSubscriptions: SessionBundle[] = [];
    const expiredSubscriptions: SessionBundle[] = [];
    subscriptions.forEach((sub) => {
      // Use the API's expiration status instead of calculating ourselves
      const isExpired = sub.isExpired || sub.status === 'expired';

      if (isExpired) {
        expiredSubscriptions.push(sub);
      } else {
        activeSubscriptions.push(sub);
      }
    });

    const activeSessionBundles = bulkSessionBundles.filter(
      (sessionBundle) => (sessionBundle.sessionsRemaining ?? 0) > 0
    );
    const activePackagePacks = packagePacks.filter((pack) => (pack.sessionsRemaining ?? 0) > 0);
    const usedPackagePacks = packagePacks.filter((pack) => (pack.sessionsRemaining ?? 0) === 0);
    const usedSessionBundles = bulkSessionBundles.filter(
      (sessionBundle) => (sessionBundle.sessionsRemaining ?? 0) === 0
    );
    const expiredItems: Array<{
      contract: SessionBundle;
      kind: 'subscription' | 'sessionBundle' | 'packagePack';
    }> = [
      ...expiredSubscriptions.map((contract) => ({ contract, kind: 'subscription' as const })),
      ...usedSessionBundles.map((contract) => ({ contract, kind: 'sessionBundle' as const })),
      ...usedPackagePacks.map((contract) => ({ contract, kind: 'packagePack' as const })),
    ];

    return (
      <div className="space-y-8">
        <h2 className="mb-4 text-2xl font-semibold">{t.account.mySubscription}</h2>
        {activeSubscriptions.length === 0 ? (
          <div className="py-8 text-center text-gray-500">{t.account.noActiveSubscription}</div>
        ) : (
          activeSubscriptions.map((sub) => {
            const isCancelled =
              sub.isCancelled ||
              (sub.metaData?.stripe_cancel_at_period_end === true &&
                sub.metaData?.stripe_canceled_at);

            return (
              <div key={sub.id} className="mb-6 rounded-lg bg-white p-6 shadow">
                <h3 className="mb-2 text-xl font-bold">
                  {getTranslatedField(sub, 'name', language)}
                </h3>
                <div className="my-4 grid grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <p className="text-sm text-gray-500">{t.account.sessionsRemaining}</p>
                    <p
                      className={`text-2xl font-bold ${sub.sessionsRemaining === 0 ? 'text-red-600' : 'text-gray-800'}`}
                    >
                      {sub.sessionsRemaining ?? 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t.account.sessionsUsed || 'Sessions used/booked'}
                    </p>
                    <p className="text-2xl font-bold text-gray-800">{sub.usedSessions ?? 0}</p>
                  </div>
                  {sub.rolledOverSessions && sub.rolloverExpirationDate && (
                    <>
                      <div>
                        <p className="text-sm text-gray-500">{t.account.sessionsRolledOver}</p>
                        <p className="text-2xl font-bold text-gray-800">{sub.rolledOverSessions}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t.account.expiresOn}</p>
                        <p className="font-semibold text-gray-800">
                          {formatDate(sub.rolloverExpirationDate)}
                        </p>
                      </div>
                    </>
                  )}
                  {sub.renewalDate && sub.recurring && sub.status === 'active' && !isCancelled && (
                    <div className="col-span-2 mt-2 border-t border-gray-100 pt-2">
                      <p className="text-sm text-gray-500">{t.account.renewsOn}</p>
                      <p className="text-base font-medium text-gray-800">
                        {formatDate(sub.renewalDate)}
                      </p>
                      {sub.renewalPrice && (
                        <p className="text-sm text-gray-600">
                          at {formatPrice(sub.renewalPrice)}
                          {sub.originalPrice && sub.originalPrice > sub.renewalPrice && (
                            <span className="ml-2 text-gray-400 line-through">
                              {formatPrice(sub.originalPrice)}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                {sub.recurring && sub.status === 'active' && !isCancelled ? (
                  <>
                    {sub.renewalDate && (
                      <div className="mb-4 border-b border-gray-200 pb-4">
                        <p className="text-xs text-gray-500">
                          {t.account.unusedSessionsNote ||
                            'Your unused sessions will roll over for up to 3 months.'}
                        </p>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setSubscriptionToCancel(sub);
                        setShowCancelSubscriptionModal(true);
                      }}
                      className="rounded border border-gray-300 bg-transparent px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {t.account.cancelSubscription}
                    </button>
                  </>
                ) : (
                  <p className="text-gray-600">
                    {t.account.subscriptionExpires.replace(
                      '{{endDate}}',
                      new Date(sub.ends_at || '').toLocaleDateString(language, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    )}
                  </p>
                )}
              </div>
            );
          })
        )}

        <h2 className="mb-4 text-2xl font-semibold">{t.account.mySessionBundle}</h2>
        {loadingContracts ? (
          <div className="py-8 text-center text-gray-500">
            {t.account.loadingSessionBundles || 'Loading session bundles...'}
          </div>
        ) : activeSessionBundles.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            {t.account.noActiveSessionBundles || 'No active sessionBundles'}
          </div>
        ) : (
          activeSessionBundles.map((sessionBundle) => {
            const expiresAt = (sessionBundle as any).expires_at
              ? new Date((sessionBundle as any).expires_at)
              : (sessionBundle as any).expirationDate
                ? new Date((sessionBundle as any).expirationDate)
                : null;
            const now = new Date();
            const twoYearsFromNow = new Date();
            twoYearsFromNow.setFullYear(now.getFullYear() + 2);
            const showExpiration = expiresAt && expiresAt < twoYearsFromNow;

            return (
              <div key={sessionBundle.id} className="rounded-lg border bg-white p-6">
                <h3 className="mb-2 text-xl font-bold">
                  {getTranslatedField(sessionBundle, 'name', language)}
                </h3>
                {sessionBundle.promotionName && (
                  <p className="mb-4 inline-block rounded-md bg-green-50 px-2 py-1 text-sm font-semibold text-green-600">
                    {getTranslatedField(sessionBundle, 'promotionName', language)}
                  </p>
                )}

                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <p className="text-sm text-gray-500">{t.account.sessionsRemaining}</p>
                    <p className="text-2xl font-bold text-gray-800">
                      {sessionBundle.sessionsRemaining ?? 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      {t.account.sessionsUsed || 'Sessions used/booked'}
                    </p>
                    <p className="text-2xl font-bold text-gray-800">
                      {sessionBundle.usedSessions ?? 0}
                    </p>
                  </div>

                  {showExpiration && (
                    <div className="col-span-2 mt-2 border-t border-gray-100 pt-2">
                      <p className="text-sm text-gray-500">{t.account.expiresOn}</p>
                      <p className="text-lg font-medium text-gray-800">
                        {formatDate(
                          (sessionBundle as any).expires_at || (sessionBundle as any).expirationDate
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}

        <h2 className="mb-4 text-2xl font-semibold">
          {t.account.myPackagePacks || 'My Package Packs'}
        </h2>
        {loadingContracts ? (
          <div className="py-8 text-center text-gray-500">
            {t.account.loadingSessionBundles || 'Loading package packs...'}
          </div>
        ) : activePackagePacks.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            {t.account.noActivePackagePacks || 'No active package packs'}
          </div>
        ) : (
          activePackagePacks.map((pack) => {
            const expiresAt = (pack as any).expires_at
              ? new Date((pack as any).expires_at)
              : (pack as any).expirationDate
                ? new Date((pack as any).expirationDate)
                : null;
            const now = new Date();
            const twoYearsFromNow = new Date();
            twoYearsFromNow.setFullYear(now.getFullYear() + 2);
            const showExpiration = expiresAt && expiresAt < twoYearsFromNow;

            return (
              <div key={pack.id} className="rounded-lg border bg-white p-6">
                <h3 className="mb-2 text-xl font-bold">
                  {getTranslatedField(pack, 'name', language)}
                </h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <p className="text-sm text-gray-500">
                      {t.account.packsRemaining || 'Packs remaining'}
                    </p>
                    <p className="text-2xl font-bold text-gray-800">
                      {pack.sessionsRemaining ?? 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.account.packsUsed || 'Packs used'}</p>
                    <p className="text-2xl font-bold text-gray-800">{pack.usedSessions ?? 0}</p>
                  </div>

                  {showExpiration && (
                    <div className="col-span-2 mt-2 border-t border-gray-100 pt-2">
                      <p className="text-sm text-gray-500">{t.account.expiresOn}</p>
                      <p className="text-lg font-medium text-gray-800">
                        {formatDate((pack as any).expires_at || (pack as any).expirationDate)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}

        {expiredItems.length > 0 && (
          <>
            <h2 className="mb-4 text-2xl font-semibold">
              {t.account.expiredSectionTitle || 'Expired Subscriptions and Used Session Bundles'}
            </h2>
            {expiredItems.map(({ contract, kind }) => {
              const isSub = kind === 'subscription';
              const isPackagePack = kind === 'packagePack';
              const effectiveEndDate = contract.ends_at || contract.renewalDate;

              return (
                <div
                  key={`${kind}-${contract.id}`}
                  className="rounded-lg border bg-white p-6 opacity-70"
                >
                  <h3 className="mb-2 text-xl font-bold text-gray-600">
                    {getTranslatedField(contract, 'name', language)}
                  </h3>
                  {contract.promotionName && (
                    <p className="mb-4 inline-block rounded-md bg-green-100 px-2 py-1 text-sm font-semibold text-green-700">
                      {getTranslatedField(contract, 'promotionName', language)}
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        {isPackagePack
                          ? t.account.packsRemaining || 'Packs remaining'
                          : t.account.sessionsRemaining}
                      </p>
                      <p className="text-2xl font-bold text-gray-600">0</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        {isPackagePack
                          ? t.account.packsUsed || 'Packs used'
                          : t.account.sessionsUsed || 'Sessions used/booked'}
                      </p>
                      <p className="text-2xl font-bold text-gray-600">
                        {contract.usedSessions ?? 0}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 border-t border-gray-200 pt-4">
                    {isSub ? (
                      <p className="text-gray-500">
                        {t.account.subscriptionExpiredOn || 'Expired on'}{' '}
                        {formatDate(effectiveEndDate || '')}
                      </p>
                    ) : (
                      <p className="text-gray-500">
                        {isPackagePack
                          ? t.account.usedPackagePacksTitle || 'Used Package Packs'
                          : t.account.sessionBundleFullyUsed || 'Session bundle fully used'}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  const renderBookingsTab = () => {
    const now = new Date();

    const getBookingMeta = (booking: Booking) => {
      return (booking.metadata || booking.metaData || {}) as Record<string, any>;
    };

    const getBookingPackageInfo = (booking: Booking) => {
      const meta = getBookingMeta(booking);
      const packageGroupId =
        typeof meta.package_group_id === 'string' && meta.package_group_id.trim().length > 0
          ? meta.package_group_id
          : null;

      if (!packageGroupId) {
        return null;
      }

      const packageName =
        (typeof meta.package_name === 'string' && meta.package_name.trim()) ||
        (typeof meta.package_name_en === 'string' && meta.package_name_en.trim()) ||
        'Package';

      return {
        packageGroupId,
        packageName,
      };
    };

    // Filter bookings based on search term
    const filteredBookings = bookings.filter((booking) => {
      const serviceName = getTranslatedField(booking.service, 'name', language);
      const packageInfo = getBookingPackageInfo(booking);
      const searchLower = searchTerm.toLowerCase();
      return (
        serviceName.toLowerCase().includes(searchLower) ||
        (packageInfo?.packageName || '').toLowerCase().includes(searchLower) ||
        booking.date.includes(searchTerm) ||
        booking.time.includes(searchTerm)
      );
    });

    const upcomingBookings = filteredBookings
      .filter((b) => {
        const bookingDateTime = new Date(`${b.date}T${b.time}`);
        return b.status === BOOKING_STATUS.CONFIRMED && bookingDateTime >= now;
      })
      .sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.time}`);
        const dateB = new Date(`${b.date}T${b.time}`);
        return dateA.getTime() - dateB.getTime();
      });

    const pastBookings = filteredBookings
      .filter((b) => {
        const bookingDateTime = new Date(`${b.date}T${b.time}`);
        return b.status === BOOKING_STATUS.CONFIRMED && bookingDateTime < now;
      })
      .sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.time}`);
        const dateB = new Date(`${b.date}T${b.time}`);
        return dateB.getTime() - dateA.getTime();
      });

    const cancelledBookings = filteredBookings
      .filter((b) => b.status === BOOKING_STATUS.CANCELLED)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // Get filtered bookings based on selected filter
    const getFilteredBookings = () => {
      switch (bookingFilter) {
        case 'upcoming':
          return { upcoming: upcomingBookings, past: [], cancelled: [] };
        case 'past':
          return { upcoming: [], past: pastBookings, cancelled: [] };
        case 'cancelled':
          return { upcoming: [], past: [], cancelled: cancelledBookings };
        case 'all':
          return { upcoming: upcomingBookings, past: pastBookings, cancelled: cancelledBookings };
        default:
          return { upcoming: upcomingBookings, past: pastBookings, cancelled: cancelledBookings };
      }
    };

    const { upcoming, past, cancelled } = getFilteredBookings();

    type BookingRenderGroup = {
      key: string;
      packageGroupId: string | null;
      packageName: string | null;
      bookings: Booking[];
    };

    const groupBookingsByPackage = (bookingList: Booking[]): BookingRenderGroup[] => {
      const groupedResults: BookingRenderGroup[] = [];
      const packageGroupIndexMap = new Map<string, number>();

      bookingList.forEach((booking) => {
        const packageInfo = getBookingPackageInfo(booking);

        if (!packageInfo) {
          groupedResults.push({
            key: `single-${booking.id}`,
            packageGroupId: null,
            packageName: null,
            bookings: [booking],
          });
          return;
        }

        const existingIndex = packageGroupIndexMap.get(packageInfo.packageGroupId);
        if (existingIndex === undefined) {
          groupedResults.push({
            key: `package-${packageInfo.packageGroupId}`,
            packageGroupId: packageInfo.packageGroupId,
            packageName: packageInfo.packageName,
            bookings: [booking],
          });
          packageGroupIndexMap.set(packageInfo.packageGroupId, groupedResults.length - 1);
          return;
        }

        groupedResults[existingIndex].bookings.push(booking);
      });

      return groupedResults;
    };

    const upcomingGroups = groupBookingsByPackage(upcoming);
    const pastGroups = groupBookingsByPackage(past);
    const cancelledGroups = groupBookingsByPackage(cancelled);

    const getServiceIcon = (serviceName: string) => {
      const name = serviceName.toLowerCase();
      if (name.includes('infrared') && name.includes('sauna')) {
        return (
          <svg
            className="h-6 w-6 text-orange-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        );
      } else if (name.includes('cryotherapy')) {
        return (
          <svg
            className="h-6 w-6 text-cyan-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 2L8 8l4 2-4 2 4 6 4-6-4-2 4-2-4-6zm0 0v4m-4 2l3.5 1.75M8 10l3.5 1.75M12 22v-4m4-4l-3.5-1.75M16 14l-3.5-1.75"
            />
          </svg>
        );
      } else if (name.includes('red light')) {
        return (
          <svg
            className="h-6 w-6 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
        );
      } else if (name.includes('cold plunge') || name.includes('plunge')) {
        return (
          <svg
            className="h-6 w-6 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
            />
          </svg>
        );
      } else if (name.includes('celluma') || name.includes('facial')) {
        return (
          <svg
            className="h-6 w-6 text-pink-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
            />
          </svg>
        );
      } else if (name.includes('compression')) {
        return (
          <svg
            className="h-6 w-6 text-purple-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
        );
      } else if (name.includes('contrast')) {
        return (
          <svg
            className="h-6 w-6 text-teal-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
            />
          </svg>
        );
      } else {
        return (
          <svg
            className="h-6 w-6 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        );
      }
    };

    const renderBooking = (booking: Booking, isPast: boolean = false) => (
      <div
        key={booking.id}
        className="overflow-hidden rounded-lg border bg-white shadow-sm transition-shadow duration-200 hover:shadow-md"
      >
        <div className="p-4">
          <div className="flex items-start space-x-4">
            <div className="mt-1 flex-shrink-0">{getServiceIcon(booking.service.name)}</div>
            <div className="flex-grow">
              <div className="flex flex-col space-y-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
                <div className="flex-grow">
                  <h4 className="font-semibold text-gray-900">
                    {getTranslatedField(booking.service, 'name', language)}
                  </h4>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span>{formatDate(booking.date)}</span>
                    </div>
                    <span className="hidden text-gray-400 sm:inline">•</span>
                    <div className="flex items-center space-x-1">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>{formatTime(booking.time)}</span>
                    </div>
                    <span className="hidden text-gray-400 sm:inline">•</span>
                    <span>
                      {booking.service.duration} {t.account.minutes}
                    </span>
                    {booking.participants && booking.participants > 1 && (
                      <>
                        <span className="hidden text-gray-400 sm:inline">•</span>
                        <span>
                          {booking.participants} {t.account.participants}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Display invited participants if they exist */}
                  {((booking.metadata?.invitations?.participants &&
                    booking.metadata.invitations.participants.length > 0) ||
                    (booking.metaData?.invitations?.participants &&
                      booking.metaData.invitations.participants.length > 0)) && (
                    <div className="mt-2 text-sm text-gray-600">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-gray-500">Invited participants:</span>
                        {(
                          booking.metadata?.invitations?.participants ||
                          booking.metaData?.invitations?.participants ||
                          []
                        ).map((participant, index) => (
                          <span
                            key={index}
                            className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                              participant.status === 'accepted'
                                ? 'bg-green-100 text-green-800'
                                : participant.status === 'declined'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {participant.email}
                            {participant.status === 'accepted' && (
                              <svg className="ml-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                            {participant.status === BOOKING_STATUS.PENDING && (
                              <svg
                                className="ml-1 h-3 w-3 text-gray-500"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        booking.status === BOOKING_STATUS.CONFIRMED
                          ? 'bg-green-100 text-green-800'
                          : booking.status === BOOKING_STATUS.CANCELLED
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <>
                        {booking.status === BOOKING_STATUS.CONFIRMED && (
                          <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                        {booking.status === BOOKING_STATUS.CANCELLED && (
                          <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                        {t.account[booking.status as keyof typeof t.account] || booking.status}
                      </>
                    </span>
                    {/* Coupon session indicator */}
                    {(booking.isCouponSession || booking.couponCodeId) && (
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                        <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z"
                            clipRule="evenodd"
                          />
                          <path d="M9 11H3v5a2 2 0 002 2h4v-7zM11 18h4a2 2 0 002-2v-5h-6v7z" />
                        </svg>
                        {t.coupon?.sessionBadge || 'Coupon'}
                      </span>
                    )}
                  </div>
                  {/* Display participant invitation status */}
                  {(() => {
                    const participants = booking.metaData?.invitations?.participants;
                    return participants &&
                      Array.isArray(participants) &&
                      participants.length > 0 ? (
                      <div className="mt-4 border-t border-gray-200 pt-4">
                        <h4 className="font-semibold text-gray-800">Invited Participants</h4>
                        <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-gray-600">
                          {participants.map((participant, index) => (
                            <li key={index}>
                              <span>{participant.email} - </span>
                              <span
                                className={`font-bold ${
                                  participant.status === 'accepted'
                                    ? 'text-green-600'
                                    : participant.status === 'declined'
                                      ? 'text-red-600'
                                      : 'text-yellow-600'
                                }`}
                              >
                                {participant.status.charAt(0).toUpperCase() +
                                  participant.status.slice(1)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null;
                  })()}
                </div>
                <div className="flex items-center space-x-3 sm:flex-col sm:items-end sm:space-x-0 sm:space-y-2">
                  {booking.status === BOOKING_STATUS.CONFIRMED &&
                    new Date(`${booking.date}T${booking.time}`) >= now && (
                      <>
                        {isWithin24Hours(booking.date, booking.time) ? (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            <div className="flex items-center">
                              <svg className="mr-1 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              {t.account.cannotReschedule24Hours || 'Cannot reschedule'}
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleReschedule(booking)}
                            className="-mx-3 -my-2 rounded px-3 py-2 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700"
                          >
                            {t.account.rescheduleBooking}
                          </button>
                        )}
                        {isWithin24Hours(booking.date, booking.time) ? (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            <div className="flex items-center">
                              <svg className="mr-1 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              {t.account.cannotCancel24Hours || 'Cannot cancel'}
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => openCancelModal(booking)}
                            className="-mx-3 -my-2 rounded px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
                          >
                            {t.account.cancelBooking}
                          </button>
                        )}
                      </>
                    )}
                  {/* {isPast && (
                    <button
                      onClick={() => {
                        //  TODO: Implement rebooking 
                      }}
                      className="-mx-3 -my-2 flex items-center rounded px-3 py-2 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700"
                    >
                      <svg
                        className="mr-1 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      {t.account.bookAgain}
                    </button>
                  )} */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    const renderBookingGroup = (group: BookingRenderGroup, isPast: boolean = false) => {
      if (!group.packageGroupId) {
        return renderBooking(group.bookings[0], isPast);
      }

      return (
        <div
          key={group.key}
          className="overflow-hidden rounded-lg border border-blue-100 bg-blue-50/40 p-4"
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
              </span>
              <h4 className="font-semibold text-blue-900">{group.packageName}</h4>
            </div>
          </div>
          <div className="space-y-3">
            {group.bookings.map((booking) => renderBooking(booking, isPast))}
          </div>
        </div>
      );
    };

    const renderEmptyState = (type: string) => {
      const messages = {
        upcoming: {
          icon: (
            <svg
              className="mx-auto mb-4 h-16 w-16 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          ),
          title: t.account.noUpcomingBookings,
          message: t.account.readyToBook,
          action: t.booking.title,
        },
        past: {
          icon: (
            <svg
              className="mx-auto mb-4 h-16 w-16 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
          title: t.account.noPastBookings,
          message: t.account.bookingHistoryWillAppear,
          action: null,
        },
        cancelled: {
          icon: (
            <svg
              className="mx-auto mb-4 h-16 w-16 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
          title: t.account.noCancelledBookings,
          message: t.account.cancelledBookingsWillAppear,
          action: null,
        },
      };

      const content = messages[type as keyof typeof messages];
      return (
        <div className="py-12 text-center">
          {content.icon}
          <h3 className="mb-2 text-lg font-medium text-gray-900">{content.title}</h3>
          <p className="mb-4 text-gray-600">{content.message}</p>
          {content.action && (
            <a
              href="/booking"
              className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              {content.action}
            </a>
          )}
        </div>
      );
    };

    const renderSkeletonLoader = () => (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse rounded-lg border bg-white p-4">
            <div className="flex items-start space-x-4">
              <div className="h-6 w-6 rounded bg-gray-200"></div>
              <div className="flex-grow">
                <div className="mb-2 h-5 w-1/3 rounded bg-gray-200"></div>
                <div className="mb-2 h-4 w-1/2 rounded bg-gray-200"></div>
                <div className="h-3 w-1/4 rounded bg-gray-200"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );

    return (
      <div className="space-y-6">
        {/* Filters and Search */}
        <div className="rounded-lg bg-gray-50 p-1 sm:p-4">
          <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setBookingFilter('upcoming')}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  bookingFilter === 'upcoming'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t.account.filterUpcoming} ({upcomingBookings.length})
              </button>
              <button
                onClick={() => setBookingFilter('past')}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  bookingFilter === 'past'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t.account.filterPast} ({pastBookings.length})
              </button>
              <button
                onClick={() => setBookingFilter('cancelled')}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  bookingFilter === 'cancelled'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t.account.filterCancelled} ({cancelledBookings.length})
              </button>
              <button
                onClick={() => setBookingFilter('all')}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  bookingFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t.account.filterAll} ({bookings.length})
              </button>
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder={t.account.searchBookings}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:ring-blue-500 sm:w-64"
              />
              <svg
                className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {loadingBookings ? (
          renderSkeletonLoader()
        ) : (
          <>
            {upcoming.length > 0 && (
              <div>
                <h3 className="mb-4 flex items-center text-lg font-semibold">
                  <svg
                    className="mr-2 h-5 w-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {t.account.upcomingBookings}
                </h3>
                <div className="space-y-3">
                  {upcomingGroups.map((group) => renderBookingGroup(group))}
                </div>
              </div>
            )}
            {past.length > 0 && (
              <div>
                <h3 className="mb-4 flex items-center text-lg font-semibold">
                  <svg
                    className="mr-2 h-5 w-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {t.account.pastBookings}
                </h3>
                <div className="space-y-3">
                  {pastGroups.map((group) => renderBookingGroup(group, true))}
                </div>
              </div>
            )}
            {cancelled.length > 0 && (
              <div>
                <h3 className="mb-4 flex items-center text-lg font-semibold">
                  <svg
                    className="mr-2 h-5 w-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {t.account.cancelledBookings}
                </h3>
                <div className="space-y-3">
                  {cancelledGroups.map((group) => renderBookingGroup(group))}
                </div>
              </div>
            )}
            {bookingFilter === 'upcoming' && upcoming.length === 0 && renderEmptyState('upcoming')}
            {bookingFilter === 'past' && past.length === 0 && renderEmptyState('past')}
            {bookingFilter === 'cancelled' &&
              cancelled.length === 0 &&
              renderEmptyState('cancelled')}
            {bookingFilter === 'all' &&
              upcoming.length === 0 &&
              past.length === 0 &&
              cancelled.length === 0 && (
                <div className="py-12 text-center">
                  <svg
                    className="mx-auto mb-4 h-16 w-16 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <h3 className="mb-2 text-lg font-medium text-gray-900">
                    {t.account.noBookingsYet}
                  </h3>
                  <a
                    href="/booking"
                    className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    {t.account.bookYourFirstSession}
                  </a>
                </div>
              )}
          </>
        )}
      </div>
    );
  };

  const renderCancelModal = () => {
    if (!isCancelModalOpen || !bookingToCancel) return null;
    const message = t.account.cancelBookingConfirmMessage
      .replace('{{serviceName}}', getTranslatedField(bookingToCancel.service, 'name', language))
      .replace('{{date}}', formatDate(bookingToCancel.date))
      .replace('{{time}}', bookingToCancel.time);

    const isCouponPair = couponPairStatus?.requiresPairCancellation;
    const canCancelCouponPair = couponPairStatus?.canCancelPair;
    const couponBlocked = isCouponPair && !canCancelCouponPair;
    const refundAmount = couponPairStatus?.refundAmountCents
      ? formatPrice(couponPairStatus.refundAmountCents / 100)
      : formatPrice(0);

    return (
      <div className="fixed inset-0 z-50 flex h-full w-full items-center justify-center overflow-y-auto bg-gray-600 bg-opacity-50">
        <div className="relative mx-auto w-full max-w-lg rounded-md border bg-white p-5 shadow-lg">
          <div className="mt-3 text-center">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {isCouponPair
                ? t.coupon?.cancelPairTitle || 'Cancel Coupon Sessions'
                : t.account.cancelBookingConfirmTitle}
            </h3>

            {loadingCouponStatus ? (
              <div className="mt-4 py-4">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
                <p className="mt-2 text-sm text-gray-500">{t.common.loading}</p>
              </div>
            ) : (
              <>
                <div className="mt-2 px-7 py-3">
                  {isCouponPair ? (
                    <>
                      {/* Coupon pair cancellation info */}
                      <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-left">
                        <div className="mb-2 flex items-center">
                          <svg
                            className="mr-2 h-5 w-5 text-amber-500"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="font-medium text-amber-800">
                            {t.coupon?.linkedBookingNotice || 'Linked Coupon Booking'}
                          </span>
                        </div>
                        <p className="text-sm text-amber-700">
                          {t.coupon?.pairCancellationExplanation ||
                            'This booking is part of a coupon pair. Both sessions must be cancelled together for a full refund.'}
                        </p>
                      </div>

                      {couponBlocked ? (
                        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-left">
                          <p className="text-sm text-red-700">
                            {couponPairStatus?.hasUsedSession
                              ? t.coupon?.errors?.sessionAlreadyUsed ||
                                'Cannot cancel: one or more linked sessions have already been used. You may still be able to reschedule within 24 hours of the appointment.'
                              : t.coupon?.errors?.cannotCancel ||
                                'Unable to cancel these sessions at this time.'}
                          </p>
                        </div>
                      ) : (
                        <>
                          <p className="mb-3 text-sm text-gray-600">
                            {(
                              t.coupon?.pairCancellationConfirm ||
                              'Cancelling will cancel both coupon sessions and refund {{amount}}. Your coupon code will be restored for future use.'
                            ).replace('{{amount}}', refundAmount)}
                          </p>
                          {couponPairStatus?.linkedBookings &&
                            couponPairStatus.linkedBookings.length > 1 && (
                              <div className="mb-3 text-left">
                                <p className="mb-1 text-xs text-gray-500">
                                  {t.coupon?.linkedSessions || 'Linked sessions to cancel:'}
                                </p>
                                <ul className="space-y-1 text-xs text-gray-600">
                                  {couponPairStatus.linkedBookings.map(
                                    (lb: {
                                      id: string;
                                      startTime: string;
                                      serviceName?: string;
                                      serviceNameEt?: string;
                                      serviceNameFi?: string;
                                      serviceNameRu?: string;
                                    }) => {
                                      const bookingDate = new Date(lb.startTime);
                                      const dateLocale =
                                        language === 'ru'
                                          ? ruLocale
                                          : language === 'et'
                                            ? etLocale
                                            : language === 'fi'
                                              ? fiLocale
                                              : undefined;
                                      const dateStr = formatInTimeZone(
                                        bookingDate,
                                        TALLINN_TIMEZONE,
                                        'EEE, MMMM d, yyyy',
                                        { locale: dateLocale }
                                      );
                                      const timeStr = formatInTimeZone(
                                        bookingDate,
                                        TALLINN_TIMEZONE,
                                        'HH:mm'
                                      );
                                      // Get service name based on current language
                                      let serviceName =
                                        lb.serviceName || t.account.session || 'Session';
                                      if (language === 'et' && lb.serviceNameEt) {
                                        serviceName = lb.serviceNameEt;
                                      } else if (language === 'fi' && lb.serviceNameFi) {
                                        serviceName = lb.serviceNameFi;
                                      } else if (language === 'ru' && lb.serviceNameRu) {
                                        serviceName = lb.serviceNameRu;
                                      }
                                      return (
                                        <li key={lb.id} className="flex items-center">
                                          <span className="mr-2 h-2 w-2 rounded-full bg-gray-400"></span>
                                          {serviceName} - {dateStr}, {timeStr}
                                        </li>
                                      );
                                    }
                                  )}
                                </ul>
                              </div>
                            )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-500">{message}</p>
                      <p className="mt-2 text-xs text-gray-400">
                        {t.account.cancellationPolicyNotice ||
                          'Note: Bookings cannot be cancelled within 24 hours of the appointment time.'}
                      </p>
                    </>
                  )}
                </div>
                {cancelError && <p className="mt-2 text-sm text-red-500">{cancelError}</p>}
                <div className="items-center px-4 py-3">
                  {!couponBlocked && (
                    <button
                      id="confirm-cancel-btn"
                      onClick={handleConfirmCancel}
                      disabled={isCancelling}
                      className="w-auto rounded-md bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      {isCancelling
                        ? t.common.loading
                        : isCouponPair
                          ? t.coupon?.cancelBothSessions || 'Cancel Both Sessions'
                          : t.account.cancelBookingYes}
                    </button>
                  )}
                  {couponBlocked && couponPairStatus?.hasUsedSession && (
                    <button
                      onClick={() => {
                        closeCancelModal();
                        handleReschedule(bookingToCancel);
                      }}
                      className="w-auto rounded-md bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {t.account.rescheduleBooking || 'Reschedule Instead'}
                    </button>
                  )}
                  <button
                    id="cancel-btn"
                    onClick={closeCancelModal}
                    className="ml-3 w-auto rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-900 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {t.account.cancelBookingNo}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      {successMessage && (
        <div className="mb-6 rounded-md border border-green-200 bg-green-50 p-4">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">{t.account.title}</h1>
        <button
          onClick={() => {
            localStorage.removeItem('auth_token');
            logout({ logoutParams: { returnTo: window.location.origin } });
          }}
          className="rounded-md border border-red-600 bg-transparent px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
        >
          {t.nav.logout}
        </button>
      </div>

      {upcomingBookings.length > 0 && (
        <div className="mb-8 rounded-lg bg-white p-6 shadow">
          <h2 className="mb-4 text-xl font-bold text-gray-800">{t.account.upcomingBookings}</h2>
          <ul className="space-y-3">
            {upcomingBookings.slice(0, 2).map((booking) => (
              <li key={booking.id} className="flex items-center justify-between">
                <div>
                  <span className="text-gray-700">
                    {getTranslatedField(booking.service, 'name', language)}
                  </span>
                  <br />
                  <span className="text-sm font-medium">
                    {formatDate(booking.date)} @ {formatTime(booking.time)}
                  </span>
                </div>
                <div className="space-x-2">
                  {isWithin24Hours(booking.date, booking.time) ? (
                    <span className="text-sm text-gray-500">
                      {t.account.cannotReschedule24Hours || 'Cannot reschedule'}
                    </span>
                  ) : (
                    <button
                      onClick={() => handleReschedule(booking)}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      {t.account.rescheduleBooking}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <button
            onClick={() => setActiveTab('bookings')}
            className="mt-4 flex items-center gap-1 text-sm text-blue-600 hover:underline"
          >
            {t.account.viewAllBookings}
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      <div className="rounded-lg bg-white shadow">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-6 py-4 text-sm font-medium ${
                activeTab === 'profile'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.account.profile}
            </button>
            <button
              onClick={() => setActiveTab('sessionBundles')}
              className={`relative px-6 py-4 text-sm font-medium ${
                activeTab === 'sessionBundles'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.account.mySessionBundlesAndSubscriptions}
              {contractsCount > 0 && (
                <span className="absolute -right-1 top-3 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[11px] font-bold leading-none text-white">
                  {contractsCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('bookings')}
              className={`relative px-6 py-4 text-sm font-medium ${
                activeTab === 'bookings'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.account.bookings}
              {upcomingBookings.length > 0 && (
                <span className="absolute -right-1 top-3 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[11px] font-bold leading-none text-white">
                  {upcomingBookings.length}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="px-4 py-6 lg:px-4">
          {activeTab === 'profile' && renderProfileTab()}
          {activeTab === 'sessionBundles' && renderSessionBundlesAndSubscriptionsTab()}
          {activeTab === 'bookings' && renderBookingsTab()}
        </div>
      </div>
      {renderCancelModal()}

      {/* Add Confirmation Modal for Subscription Cancellation */}
      {showCancelSubscriptionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-xl font-bold">{t.account.cancelConfirmTitle}</h2>
            <p className="mb-4">
              {t.account.cancelConfirmMessage.replace(
                '{{endDate}}',
                new Date(
                  subscriptionToCancel?.ends_at || subscriptionToCancel?.renewalDate || ''
                ).toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' })
              )}
            </p>
            {cancelSubscriptionError && (
              <p className="mb-4 text-sm text-red-500">{cancelSubscriptionError}</p>
            )}
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setShowCancelSubscriptionModal(false);
                  setSubscriptionToCancel(null);
                }}
                disabled={isCancellingSubscription}
                className="rounded bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300"
              >
                {t.account.keep}
              </button>
              <button
                onClick={handleConfirmCancelSubscription}
                disabled={isCancellingSubscription}
                className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:bg-red-300"
              >
                {isCancellingSubscription ? t.common.loading : t.account.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountPage;
